import React, { useEffect, useState } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import {
  getVisitStats, getDailyVisits, getMonthlyVisits,
  getTopArticles, getAllEPapers, getVisitsByPageType, getActiveVisitors
} from '../../firebase/firestore';
import './Analytics.css';

const COLORS = ['#c0392b','#2563eb','#16a34a','#7c3aed','#ea580c','#0891b2'];

export default function Analytics() {
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [topArticles, setTopArticles] = useState([]);
  const [topEPapers, setTopEPapers] = useState([]);
  const [pageTypes, setPageTypes] = useState([]);
  const [activeVisitors, setActiveVisitors] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const [s, d, m, ta, ep, pt, av] = await Promise.all([
      getVisitStats(),
      getDailyVisits(30),
      getMonthlyVisits(12),
      getTopArticles(10),
      getAllEPapers(),
      getVisitsByPageType(),
      getActiveVisitors()
    ]);
    setStats(s);
    setDaily(d);
    setMonthly(m);
    setTopArticles(ta);
    setTopEPapers(ep.sort((a,b) => (b.viewCount||0) - (a.viewCount||0)).slice(0,7));
    setPageTypes(pt);
    setActiveVisitors(av);
    setLoading(false);
  };

  useEffect(() => {
    load();
    const interval = setInterval(async () => {
      const av = await getActiveVisitors();
      setActiveVisitors(av);
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) return <div className="loading-spinner">Loading analytics...</div>;

  const STAT_ROWS = [
    { label: 'Today', data: stats?.today },
    { label: 'This Week', data: stats?.week },
    { label: 'This Month', data: stats?.month },
    { label: 'This Year', data: stats?.year },
    { label: 'All Time', data: stats?.allTime },
  ];

  return (
    <div className="analytics">
      <div className="page-header-row">
        <div>
          <h1 className="page-title">📈 Analytics</h1>
          <p className="page-subtitle">Website visit statistics and performance insights</p>
        </div>
        <div className="active-badge">
          <span className="active-dot" />
          <strong>{activeVisitors}</strong> active visitors now
        </div>
      </div>

      {/* Summary Cards */}
      <div className="analytics-summary-grid">
        {STAT_ROWS.map(row => (
          <div key={row.label} className="analytics-stat-card">
            <div className="asc-label">{row.label}</div>
            <div className="asc-total">{(row.data?.total || 0).toLocaleString()}</div>
            <div className="asc-breakdown">
              <span>🌐 {(row.data?.site || 0).toLocaleString()} site</span>
              <span>📰 {(row.data?.epaper || 0).toLocaleString()} e-paper</span>
            </div>
          </div>
        ))}
      </div>

      {/* Daily Line Chart */}
      <div className="chart-card">
        <h3 className="chart-title">📅 Daily Visits — Last 30 Days</h3>
        <ResponsiveContainer width="100%" height={280}>
          <LineChart data={daily} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e0" />
            <XAxis dataKey="date" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} interval={4} />
            <YAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} />
            <Tooltip contentStyle={{ fontFamily: 'JetBrains Mono', fontSize: '0.8rem' }} />
            <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '0.8rem' }} />
            <Line type="monotone" dataKey="site" stroke="#c0392b" strokeWidth={2} dot={false} name="Main Site" />
            <Line type="monotone" dataKey="epaper" stroke="#2563eb" strokeWidth={2} dot={false} name="E-Paper" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {/* Monthly Bar Chart */}
      <div className="chart-card">
        <h3 className="chart-title">📆 Monthly Visits — Last 12 Months</h3>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={monthly} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e8e8e0" />
            <XAxis dataKey="month" tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} />
            <YAxis tick={{ fontSize: 10, fontFamily: 'JetBrains Mono' }} />
            <Tooltip contentStyle={{ fontFamily: 'JetBrains Mono', fontSize: '0.8rem' }} />
            <Legend wrapperStyle={{ fontFamily: 'JetBrains Mono', fontSize: '0.8rem' }} />
            <Bar dataKey="site" fill="#c0392b" name="Main Site" radius={[3,3,0,0]} />
            <Bar dataKey="epaper" fill="#2563eb" name="E-Paper" radius={[3,3,0,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="analytics-bottom-grid">
        {/* Pie Chart */}
        <div className="chart-card">
          <h3 className="chart-title">🍕 Visits by Page Type</h3>
          {pageTypes.length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart>
                <Pie data={pageTypes} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                  {pageTypes.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={{ fontFamily: 'JetBrains Mono', fontSize: '0.8rem' }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <div className="empty-state" style={{ padding: '40px' }}><p>No visit data yet</p></div>}
        </div>

        {/* Top E-Papers */}
        <div className="chart-card">
          <h3 className="chart-title">📄 Most Viewed E-Papers</h3>
          <div className="top-list">
            {topEPapers.length === 0
              ? <div className="empty-state" style={{ padding: '30px' }}><p>No e-paper data yet</p></div>
              : topEPapers.map((ep, i) => (
                <div key={ep.id} className="top-list-item">
                  <span className="top-rank">{String(i+1).padStart(2,'0')}</span>
                  <span className="top-name">📅 {ep.date}</span>
                  <span className="top-val">👁 {ep.viewCount || 0}</span>
                </div>
              ))
            }
          </div>
        </div>
      </div>

      {/* Top Articles Table */}
      <div className="chart-card">
        <h3 className="chart-title">🔥 Top 10 Most Viewed Articles</h3>
        {topArticles.length === 0
          ? <div className="empty-state" style={{ padding: '30px' }}><p>No article data yet</p></div>
          : (
            <div className="top-articles-table-wrap">
              <table className="top-articles-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Title</th>
                    <th>Category</th>
                    <th>Author</th>
                    <th>Views</th>
                  </tr>
                </thead>
                <tbody>
                  {topArticles.map((a, i) => (
                    <tr key={a.id}>
                      <td><span className="rank-num">{i + 1}</span></td>
                      <td className="article-title-cell"><a href={`/article/${a.id}`} target="_blank" rel="noopener noreferrer">{a.title}</a></td>
                      <td><span className="badge" style={{ fontSize: '0.65rem' }}>{a.category}</span></td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{a.author}</td>
                      <td><span className="view-count">👁 {(a.viewCount || 0).toLocaleString()}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        }
      </div>
    </div>
  );
}
